import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Newspaper, Image, FileText, LogOut, ExternalLink, Rss, KeyRound, X, Check, Gauge } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { changePassword } from "../../api";
import ArticleManager from "./ArticleManager";
import BannerManager from "./BannerManager";
import ComunicazioniManager from "./ComunicazioniManager";
import FeedManager from "./FeedManager";
import BilancioManager from "./BilancioManager";

const TABS = [
  { key: "articoli", label: "Articoli", icon: Newspaper },
  { key: "banner", label: "Banner", icon: Image },
  { key: "comunicazioni", label: "Comunicazioni", icon: FileText },
  { key: "bilanci", label: "Bilancio 60s", icon: Gauge },
  { key: "feed", label: "Feed RSS", icon: Rss },
];

const AdminDashboard = () => {
  const { email, logout } = useAuth();
  const [tab, setTab] = useState("articoli");
  const [pwOpen, setPwOpen] = useState(false);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwMsg, setPwMsg] = useState(null); // {type, text}
  const [pwBusy, setPwBusy] = useState(false);

  const submitPw = async (e) => {
    e.preventDefault();
    setPwMsg(null);
    if (pw.next.length < 8) {
      setPwMsg({ type: "error", text: "La nuova password deve avere almeno 8 caratteri." });
      return;
    }
    if (pw.next !== pw.confirm) {
      setPwMsg({ type: "error", text: "Le due nuove password non coincidono." });
      return;
    }
    setPwBusy(true);
    try {
      await changePassword(pw.current, pw.next);
      setPwMsg({ type: "success", text: "Password aggiornata con successo." });
      setPw({ current: "", next: "", confirm: "" });
      setTimeout(() => setPwOpen(false), 1200);
    } catch (err) {
      setPwMsg({
        type: "error",
        text: err?.response?.data?.detail || "Errore durante l'aggiornamento.",
      });
    } finally {
      setPwBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-sq-light">
      {/* Top bar */}
      <header className="bg-sq-navy text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-serif-display text-xl">
              Società <span className="text-sq-orange">Quotate</span>
            </span>
            <span className="text-xs bg-white/10 px-2 py-0.5 rounded-sm uppercase tracking-wide">Admin</span>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <Link to="/" target="_blank" className="flex items-center gap-1 text-gray-300 hover:text-white">
              <ExternalLink size={14} /> Vedi il sito
            </Link>
            <span className="text-gray-300 hidden sm:inline">{email}</span>
            <button
              onClick={() => { setPwOpen(true); setPwMsg(null); }}
              className="flex items-center gap-1 bg-white/10 hover:bg-sq-orange transition-colors px-3 py-1.5 rounded-sm"
            >
              <KeyRound size={14} /> Cambia password
            </button>
            <button
              onClick={logout}
              className="flex items-center gap-1 bg-white/10 hover:bg-sq-orange transition-colors px-3 py-1.5 rounded-sm"
            >
              <LogOut size={14} /> Esci
            </button>
          </div>
        </div>
      </header>

      {/* Change password modal */}
      {pwOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setPwOpen(false)} />
          <form
            onSubmit={submitPw}
            className="relative bg-white rounded-md shadow-2xl w-full max-w-md p-6 fade-in"
          >
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-serif-display text-xl text-sq-navy flex items-center gap-2">
                <KeyRound size={18} className="text-sq-orange" /> Cambia password
              </h3>
              <button type="button" onClick={() => setPwOpen(false)} className="text-gray-400 hover:text-sq-navy">
                <X size={20} />
              </button>
            </div>

            {pwMsg && (
              <div
                className={`mb-4 text-sm rounded-sm px-3 py-2 flex items-center gap-2 ${
                  pwMsg.type === "success"
                    ? "bg-emerald-50 border border-emerald-200 text-emerald-700"
                    : "bg-red-50 border border-red-200 text-red-600"
                }`}
              >
                {pwMsg.type === "success" && <Check size={14} />} {pwMsg.text}
              </div>
            )}

            <label className="block text-sm font-medium text-sq-navy mb-1">Password attuale</label>
            <input
              type="password"
              value={pw.current}
              onChange={(e) => setPw({ ...pw, current: e.target.value })}
              required
              className="w-full border border-sq rounded-sm px-3 py-2.5 text-sm outline-none focus:border-sq-orange mb-4"
            />
            <label className="block text-sm font-medium text-sq-navy mb-1">Nuova password (min. 8 caratteri)</label>
            <input
              type="password"
              value={pw.next}
              onChange={(e) => setPw({ ...pw, next: e.target.value })}
              required
              className="w-full border border-sq rounded-sm px-3 py-2.5 text-sm outline-none focus:border-sq-orange mb-4"
            />
            <label className="block text-sm font-medium text-sq-navy mb-1">Conferma nuova password</label>
            <input
              type="password"
              value={pw.confirm}
              onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
              required
              className="w-full border border-sq rounded-sm px-3 py-2.5 text-sm outline-none focus:border-sq-orange mb-6"
            />
            <div className="flex gap-3">
              <button
                type="submit"
                disabled={pwBusy}
                className="bg-sq-orange hover:bg-sq-orange-dark transition-colors text-white px-5 py-2.5 rounded-sm text-sm font-medium disabled:opacity-60"
              >
                {pwBusy ? "Aggiornamento..." : "Aggiorna password"}
              </button>
              <button
                type="button"
                onClick={() => setPwOpen(false)}
                className="border border-sq text-sq-navy px-5 py-2.5 rounded-sm text-sm hover:bg-sq-light"
              >
                Annulla
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white border-b border-sq">
        <div className="max-w-7xl mx-auto px-4 md:px-6 flex gap-1">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`flex items-center gap-2 px-4 py-4 text-sm font-medium border-b-2 transition-colors ${
                  tab === t.key
                    ? "border-sq-orange text-sq-orange"
                    : "border-transparent text-sq-navy hover:text-sq-orange"
                }`}
              >
                <Icon size={16} /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {tab === "articoli" && <ArticleManager />}
        {tab === "banner" && <BannerManager />}
        {tab === "comunicazioni" && <ComunicazioniManager />}
        {tab === "bilanci" && <BilancioManager />}
        {tab === "feed" && <FeedManager />}
      </main>
    </div>
  );
};

export default AdminDashboard;
